import React from 'react';

import { Badge, FormattedMoney, Icon } from '@meterup/atto';
import { Quote } from '@meterup/connect-api';
import cx from 'classnames';

import { formatBandwidth } from '../../../utils/formatBandwidth';
import { formatDataCap } from '../../../utils/formatDataCap';
import { formatSelectionPriority } from '../../../utils/formatSelectionPriority';
import { FormattedQuoteMonthlyAmount } from '../../Shared/FormattedMonthlyAmount/FormattedMonthlyAmount';
import FormattedSpeed from '../../Shared/FormattedSpeed';
import ProviderLogo from '../../Shared/ProviderLogo/ProviderLogo';
import ServiceabilityBadge from '../../Shared/ServiceabilityBadge/ServiceabilityBadge';

interface ConfirmQuoteBlockProps {
  quote: Quote;
  selectedQuotes: Quote[];
}

function ConfirmQuoteBlock({ quote, selectedQuotes }: ConfirmQuoteBlockProps) {
  const renderHeader = () => (
    <header className="flex justify-between items-center border-b border-gray-100 p-4">
      <div className="h-8 w-52 text-lg xl:text-2xl flex items-center justify-between gap-2">
        {quote.provider && (
          <ProviderLogo
            name={quote.provider.name}
            path={quote.provider.path}
            url={quote.provider.logoUrl}
          />
        )}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <Badge size="small" variant="neutral">
          {formatSelectionPriority(
            quote.selectionPriority ?? selectedQuotes.findIndex((q) => q.sid === quote.sid) + 1,
          )}{' '}
          connection
        </Badge>

        <ServiceabilityBadge quote={quote} size="small" />
      </div>
    </header>
  );

  const renderContent = () => (
    <div className="flex flex-col px-4 py-1 border-b border-gray-100">
      <div className="flex pb-2 gap-4">
        <div className="flex flex-col w-1/2">
          <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
            <Icon icon="arrow-down" size={14} />
            <span>
              <FormattedSpeed mbps={formatBandwidth(quote.downloadKbps)} /> download
            </span>
          </div>

          <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
            <Icon icon="arrow-up" size={14} />
            <span>
              <FormattedSpeed mbps={formatBandwidth(quote.uploadKbps)} /> upload
            </span>
          </div>

          {quote.dataCapGb > 0 && (
            <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
              <Icon icon="speed-test-high" size={14} />
              <span>{formatDataCap(quote.dataCapGb)}/mo data cap</span>
            </div>
          )}

          <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
            <Icon icon="wifi" size={14} />
            <span>{quote.connectionType?.name}</span>
          </div>
        </div>

        <div className="flex flex-col w-1/2">
          {quote.subProvider && quote.provider && quote.subProvider.sid !== quote.provider.sid && (
            <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
              <Icon icon="connection" size={14} />
              <span>Carrier: {quote.subProvider?.name}</span>
            </div>
          )}

          <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
            <Icon icon="clock" size={14} />
            <span>Est. install: {quote.installationEstimateDays} business days</span>
          </div>

          <div className="flex items-center text-xs text-gray-700 gap-2 mt-2">
            <Icon icon="company" size={14} />
            <span>Delivered to suite</span>
          </div>
        </div>
      </div>
    </div>
  );
  const renderFooter = () => (
    <footer className="flex-col w-full items-start">
      <div className="p-4 flex flex-col w-full">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col gap-2">
            <span className="text-xs">
              <span className="mr-2">
                <Icon icon="calendar" size={12} />
              </span>
              {quote.contractLengthMonths === 1
                ? 'Month-to-month'
                : `${quote.contractLengthMonths}-Month`}{' '}
              contract
            </span>
            <span className="text-xs font-normal">
              <span className="mr-2">
                <Icon icon="wrench" size={12} />
              </span>
              <FormattedMoney amount={Math.ceil(quote.installFeeCents / 100)} /> Install fee
            </span>
          </div>
          <div className="text-lg flex flex-col align-end font-bold">
            <FormattedQuoteMonthlyAmount quote={quote} />
          </div>
        </div>
      </div>
    </footer>
  );

  const quoteBlockClasses = cx(
    // Padding
    'p-0',

    // Background
    'dark:m-bg-gray-700 bg-white border-gray-100',

    // Layout
    'flex flex-col justify-between',
    'xl:flex-col xl:items-start',
  );

  return (
    <div className={quoteBlockClasses}>
      <div className="w-full">{renderHeader()}</div>
      <div className="w-full mt-2 xl:mt-0 xl:flex-3">{renderContent()}</div>
      <div className="w-full mt-2 xl:mt-0 xl:flex-1">{renderFooter()}</div>
    </div>
  );
}

export default ConfirmQuoteBlock;
